import { Actor } from "../action/Actor";
import { IdleClass } from "../action/Idle";
import { Tick } from "../action/Tick";
import { event } from "../core/EventUtil";
import { merge } from "../core/Merge";
import { Signal } from "../core/Signal";
import { KeyCode } from "../io/KeyCode";
import { FullscreenUtil } from "./FullscreenUtil";

export interface VideoControlOptions {
    video: HTMLVideoElement;
    container: HTMLElement;
    autoHide?: boolean;
    clickToggle?: boolean;
    playButton?: boolean;
    visualPlayButton?: boolean;
    controls?:
        | boolean
        | {
              toggleButton?: boolean;
              seekbar?: boolean;
              duration?: boolean;
              volumeButton?: boolean;
              fullscreen?: boolean;
          };
}

type VideoBinding = (
    video: HTMLVideoElement,
    disposables: DisposableObject[]
) => void;

export class VideoControl {
    public onStateChange = Signal.create();
    public onPlayFrame = Signal.create();
    public onTimeUpdate = Signal.create();

    private _video: HTMLVideoElement = null;
    private _element: HTMLElement;
    private _controlBar: HTMLElement;
    private _disposablesForVideo: DisposableObject[] = [];
    private _disposables: DisposableObject[] = [];
    private _items: VideoControlItem[] = [];
    private _bindings: VideoBinding[] = [];
    private _tickSlot: DisposableObject = null;
    private _handleTick = false;

    constructor(options: VideoControlOptions) {
        options = merge(
            {
                autoHide: true,
                clickToggle: true,
                visualPlayButton: false,
                playButton: true,
                controls: {
                    toggleButton: true,
                    seekbar: true,
                    duration: true,
                    volumeButton: true,
                    fullscreen: true,
                },
            },
            options
        );

        this._element = document.createElement("div");
        this._element.className = "jsidea-video-control";

        const background = document.createElement("div");
        background.className = "jsidea-video-control__background";
        this._element.appendChild(background);

        if (options.clickToggle) {
            const pauseLayer = document.createElement("button");
            pauseLayer.className = "jsidea-video-control__pause-layer";
            pauseLayer.setAttribute("data-action", "pause-layer");
            pauseLayer.setAttribute("aria-label", "Video pausieren");
            pauseLayer.tabIndex = -1;
            background.appendChild(pauseLayer);

            const playLayer = document.createElement("button");
            playLayer.className = "jsidea-video-control__play-layer";
            playLayer.setAttribute("data-action", "play-layer");
            playLayer.tabIndex = -1;
            playLayer.setAttribute("aria-label", "Video abspielen");
            background.appendChild(playLayer);
        }

        if (options.playButton) {
            const playButton = document.createElement("button");
            playButton.className = "jsidea-video-control__play-button";
            playButton.setAttribute("data-action", "play");
            playButton.setAttribute("aria-label", "Video abspielen");
            playButton.innerHTML = `<span class="jsidea-video-control__play-icon fa fa-play"></span>`;
            background.appendChild(playButton);
        }

        if (options.visualPlayButton) {
            const playButton = document.createElement("button");
            playButton.className = "jsidea-video-control__play-button jsidea-video-control__play-button--visual";
            playButton.innerHTML = `<span class="jsidea-video-control__play-icon fa fa-play"></span>`;
            background.appendChild(playButton);
        }

        this._controlBar = document.createElement("div");
        this._controlBar.className = "jsidea-video-control__bar";
        this._controlBar.classList.add("jsidea-gallery--no-drag");
        this._element.appendChild(this._controlBar);

        options.container.classList.add("jsidea-video-control-container");

        this.onStateChange.add(() => {
            this.handleTick(!this.paused);
        });

        this.video = options.video;

        if (
            options.controls === true ||
            (options.controls !== false && options.controls.toggleButton)
        ) {
            this.addItem(
                new ToggleButton({
                    videoControl: this,
                })
            );
        }

        if (
            options.controls === true ||
            (options.controls !== false && options.controls.seekbar)
        ) {
            this.addItem(
                new Seekbar({
                    videoControl: this,
                })
            );
        }

        if (
            options.controls === true ||
            (options.controls !== false && options.controls.duration)
        ) {
            this.addItem(
                new Duration({
                    videoControl: this,
                })
            );
        }

        if (
            options.controls === true ||
            (options.controls !== false && options.controls.volumeButton)
        ) {
            this.addItem(
                new VolumeButton({
                    videoControl: this,
                })
            );
        }

        if (
            options.controls === true ||
            (options.controls !== false &&
                options.controls.fullscreen &&
                FullscreenButton.isSupported)
        ) {
            this.addItem(
                new FullscreenButton({
                    videoControl: this,
                    container: options.container || document.body,
                })
            );
        }

        if (options.autoHide) {
            this._controlBar.classList.add(
                "jsidea-video-control__bar--auto-hide"
            );

            let isMouseOverControlBar = false;

            const preventIdle = (): boolean => {
                if (isMouseOverControlBar) {
                    return true;
                }

                if (
                    options.container.querySelector("*:focus-visible") !== null
                ) {
                    return true;
                }

                if (
                    this._items.some((item) => {
                        return item.preventIdle();
                    })
                ) {
                    return true;
                }
                return false;
            };

            const idle = new IdleClass({
                scope: options.container,
                duration: 3,
                preventIdle,
            });
            this._disposables.push(idle);

            event.on({
                type: ["mouseenter"],
                scope: this._controlBar,
                capture: false,
                disposables: this._disposables,
                listener: () => {
                    isMouseOverControlBar = true;
                },
            });

            event.on({
                type: ["mouseleave"],
                scope: this._controlBar,
                capture: false,
                disposables: this._disposables,
                listener: () => {
                    isMouseOverControlBar = false;
                },
            });

            event.on({
                type: ["mouseleave"],
                scope: this._element,
                capture: false,
                disposables: this._disposables,
                listener: () => {
                    if (!preventIdle()) {
                        idle.idle();
                    }
                },
            });

            // this._disposables.push(
            //     event.on({
            //         type: ["focusout"],
            //         scope: this._element,
            //         capture: false,
            //         listener: () => {
            //             if (
            //                 document.activeElement &&
            //                 options.container.contains(document.activeElement)
            //             ) {
            //                 return;
            //             }

            //             if (!preventIdle()) {
            //                 idle.idle();
            //             }
            //         },
            //     })
            // );

            idle.onIdle.add(() => {
                this._controlBar.classList.toggle(
                    "jsidea-video-control__bar--visible",
                    false
                );
            });

            idle.onReset.add(() => {
                this._controlBar.classList.toggle(
                    "jsidea-video-control__bar--visible",
                    true
                );
            });
        }

        options.container.appendChild(this._element);

        const hasControlBar = this._controlBar.children.length > 0;

        if (hasControlBar) {
            event.on({
                type: "pointerdown",
                scope: this._element,
                selector: `[data-action="play-layer"]`,
                disposables: this._disposables,
                listener: (e) => {
                    if (e.event.button !== 0) {
                        return;
                    }
                    if (e.event.pointerType === "mouse") {
                        this._video.play();
                        return;
                    }

                    if (
                        options.autoHide &&
                        !this._controlBar.classList.contains(
                            "jsidea-video-control__bar--visible"
                        )
                    ) {
                        return;
                    }

                    this._video.play();
                },
            });
        } else {
            event.on({
                type: "click",
                scope: this._element,
                selector: `[data-action="play-layer"]`,
                disposables: this._disposables,
                listener: (e) => {
                    this._video.play();
                },
            });
        }

        event.on({
            type: "click",
            scope: this._element,
            selector: `[data-action="play"]`,
            disposables: this._disposables,
            listener: (e) => {
                this._video.play();
            },
        });

        if (hasControlBar) {
            event.on({
                type: "pointerdown",
                scope: this._element,
                selector: `[data-action="pause-layer"]`,
                disposables: this._disposables,
                listener: (e) => {
                    if (e.event.button !== 0) {
                        return;
                    }
                    if (e.event.pointerType === "mouse") {
                        this._video.pause();
                        return;
                    }

                    if (
                        options.autoHide &&
                        !this._controlBar.classList.contains(
                            "jsidea-video-control__bar--visible"
                        )
                    ) {
                        return;
                    }

                    this._video.pause();
                },
            });
        } else {
            event.on({
                type: "click",
                scope: this._element,
                selector: `[data-action="pause-layer"]`,
                disposables: this._disposables,
                listener: (e) => {
                    this._video.pause();
                },
            });
        }

        event.on({
            type: "click",
            scope: this._element,
            selector: `[data-action="pause"]`,
            disposables: this._disposables,
            listener: (event) => {
                this._video.pause();
            },
        });
    }

    public bindToVideo(callback: VideoBinding): DisposableObject {
        this._bindings.push(callback);

        if (this._video) {
            callback(this._video, this._disposablesForVideo);
        }

        return {
            dispose: () => {
                const index = this._bindings.indexOf(callback);
                if (index !== -1) {
                    this._bindings.splice(index, 1);
                }
            },
        };
    }

    public get element(): HTMLElement {
        return this._element;
    }

    public get video(): HTMLVideoElement {
        return this._video;
    }

    public set video(value: HTMLVideoElement) {
        if (this._video === value) {
            return;
        }
        this._video = value;

        this._disposablesForVideo.forEach((d) => d.dispose());
        this._disposablesForVideo.length = 0;

        if (this._video) {
            this.paused = this._video.paused;
            this.ended = this._video.ended;
            this.waiting = this._video.readyState < 2;

            this._disposablesForVideo.push(
                event.on({
                    type: "play",
                    scope: this._video,
                    listener: () => {
                        this.paused = false;
                        this.ended = false;
                    },
                }),
                event.on({
                    type: "pause",
                    scope: this._video,
                    listener: () => {
                        this.paused = true;
                        this.ended = false;
                    },
                }),
                event.on({
                    type: "ended",
                    scope: this._video,
                    listener: () => {
                        this.paused = true;
                        this.ended = true;
                    },
                }),
                event.on({
                    type: "waiting",
                    scope: this._video,
                    listener: () => {
                        this.waiting = true;
                    },
                }),
                event.on({
                    type: "error",
                    scope: this._video,
                    listener: () => {
                        this.ended = true;
                        this.paused = true;
                        this.waiting = false;
                        console.error("Video error:", this._video.error);
                    },
                }),
                event.on({
                    type: "canplay",
                    scope: this._video,
                    listener: () => {
                        this.waiting = false;
                        this.ready = true;
                    },
                }),
                event.on({
                    type: "timeupdate",
                    scope: this._video,
                    listener: () => {
                        this.onTimeUpdate.dispatch();
                    },
                })
            );
        } else {
            this.ready = false;
            this.paused = false;
            this.ended = false;
            this.waiting = false;
        }

        if (this._video) {
            for (const binding of this._bindings) {
                binding(this._video, this._disposablesForVideo);
            }
        }

        this.handleTick(!this.paused);
    }

    private handleTick(b: boolean): void {
        if (this._handleTick === b) {
            return;
        }
        this._handleTick = b;

        if (this._tickSlot) {
            this._tickSlot.dispose();
            this._tickSlot = null;
        }

        if (b) {
            this.onPlayFrame.dispatch();
            this._tickSlot = Tick.add(() => {
                this.onPlayFrame.dispatch();
            });
        }
    }

    public get paused(): boolean {
        return this.hasState("paused");
    }

    private set paused(value: boolean) {
        if (this.paused === value) {
            return;
        }
        this.toggleState("paused", value);
    }

    public get ended(): boolean {
        return this.hasState("ended");
    }

    private set ended(value: boolean) {
        if (this.ended === value) {
            return;
        }
        this.toggleState("ended", value);
    }

    public get waiting(): boolean {
        return this.hasState("waiting");
    }

    private set waiting(value: boolean) {
        if (this.waiting === value) {
            return;
        }
        this.toggleState("waiting", value);
    }

    public get ready(): boolean {
        return this.hasState("ready");
    }

    private set ready(value: boolean) {
        if (this.ready === value) {
            return;
        }
        this.toggleState("ready", value);
    }

    private hasState(attr: string): boolean {
        return this._element.classList.contains(
            `jsidea-video-control--${attr}`
        );
    }

    private toggleState(attr: string, condition: boolean) {
        this._element.classList.toggle(
            `jsidea-video-control--${attr}`,
            condition
        );
        this.onStateChange.dispatch();
    }

    public addItem(...items: VideoControlItem[]): void {
        for (const item of items) {
            this._controlBar.appendChild(item.element);
            this._items.push(item);
        }
    }

    public dispose(): void {
        this.video = null;
        this.handleTick(false);
        if (this._tickSlot) {
            this._tickSlot.dispose();
            this._tickSlot = null;
        }
        this._controlBar.remove();
        this._controlBar = null;
        this.onStateChange.dispose();
        this.onStateChange = null;
        this.onPlayFrame.dispose();
        this.onPlayFrame = null;
        this.onTimeUpdate.dispose();
        this.onTimeUpdate = null;
        this._bindings.length = 0;
        this._bindings = null;
        this._disposablesForVideo = null;
        this._items.forEach((item) => item.dispose());
        this._items.length = 0;
        this._items = null;
        this._disposables.forEach((d) => d.dispose());
        this._disposables = null;
    }
}

interface VideoControlItemOptions {
    videoControl: VideoControl;
}

class VideoControlItem {
    protected _element: HTMLElement;
    protected _videoControl: VideoControl;
    protected _disposables: DisposableObject[] = [];

    constructor(options: VideoControlItemOptions) {
        this._videoControl = options.videoControl;
        this._element = document.createElement("div");
        this._element.className = "jsidea-video-control__item";
    }

    public get element(): HTMLElement {
        return this._element;
    }

    public preventIdle(): boolean {
        return false;
    }

    public dispose(): void {
        this._disposables.forEach((d) => d.dispose());
        this._disposables.length = 0;
        this._element.remove();
        this._element = null;
        this._videoControl = null;
        this._disposables = null;
    }
}

interface LabelOptions extends VideoControlItemOptions {
    label?: string;
}

class Label extends VideoControlItem {
    private _label: string;
    private _labelElement: HTMLElement;

    constructor(options: LabelOptions) {
        super(options);
        this._element.classList.add("jsidea-video-control__item--label");
        this._labelElement = document.createElement("span");
        this._labelElement.className = "jsidea-video-control__label";
        this._element.appendChild(this._labelElement);

        if (options.label !== undefined) {
            this.label = options.label;
        }
    }

    public get label(): string {
        return this._label;
    }

    public set label(value: string) {
        if (this._label === value) {
            return;
        }
        this._label = value;
        this._labelElement.textContent = this._label;
    }

    public dispose(): void {
        super.dispose();
        this._label = null;
    }
}

interface DurationOptions extends VideoControlItemOptions {}

class Duration extends Label {
    constructor(options: DurationOptions) {
        super(options);

        this._element.classList.add("jsidea-video-control__item--duration");

        this.updateTimeString();

        this._disposables.push(
            this._videoControl.onTimeUpdate.add(() => {
                this.updateTimeString();
            }),
            this._videoControl.onStateChange.add(() => {
                this.updateTimeString();
            })
        );
    }

    private updateTimeString(): void {
        if (!this._videoControl.ready) {
            this.label = "--:-- / --:--";
            return;
        }

        const currentTime = this._videoControl.video.currentTime;
        const duration = this._videoControl.video.duration;

        this.label = `${formatTime(currentTime)} / ${formatTime(duration)}`;
    }

    public dispose(): void {
        super.dispose();
    }
}

function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
}

interface VideoControlButtonOptions extends VideoControlItemOptions {
    icon?: string;
    disabled?: boolean;
}

class VideoControlButton extends VideoControlItem {
    protected _button: HTMLButtonElement;
    private _icon: string;

    constructor(options: VideoControlButtonOptions) {
        super(options);
        this._element.classList.add("jsidea-video-control__item--button");

        this._button = document.createElement("button");
        this._button.className = "jsidea-video-control__button";
        this._element.appendChild(this._button);

        if (options.icon !== undefined) {
            this.icon = options.icon;
        }
        if (options.disabled !== undefined) {
            this.disabled = options.disabled;
        }
    }

    public get disabled(): boolean {
        return this._button.disabled;
    }

    public set disabled(value: boolean) {
        this._button.disabled = value;
    }

    public get icon(): string {
        return this._icon;
    }

    public set icon(value: string) {
        if (this._icon === value) {
            return;
        }
        this._icon = value;
        this._button.innerHTML = `<span class="jsidea-video-control__button-icon ${value}"></span>`;
    }

    public dispose(): void {
        super.dispose();
        this._button.remove();
        this._button = null;
    }
}

interface SeekbarOptions extends VideoControlItemOptions {}

class Seekbar extends VideoControlItem {
    private _track: HTMLElement;
    private _fill: HTMLElement;
    private _fillRest: HTMLElement;
    private _thumb: HTMLElement;
    private _tooltip: HTMLElement;
    private _tooltipLabel: HTMLElement;
    private _actor: Actor;

    constructor(options: SeekbarOptions) {
        super(options);

        this._element.classList.add("jsidea-video-control__item--seekbar");

        this._element.innerHTML = `
            <div class="jsidea-video-control__seekbar-track">
                <div class="jsidea-video-control__seekbar-fill"></div>
                <div class="jsidea-video-control__seekbar-fill-rest"></div>
                <button class="jsidea-video-control__seekbar-thumb" type="button" aria-label="Seek">
                    <span class="jsidea-video-control__seekbar-tooltip">
                        <span class="jsidea-video-control__seekbar-tooltip-label"></span>
                    </span>
                </button>
            </div>
        `;

        this._track = this._element.querySelector(
            ".jsidea-video-control__seekbar-track"
        );
        this._fill = this._element.querySelector(
            ".jsidea-video-control__seekbar-fill"
        );
        this._fillRest = this._element.querySelector(
            ".jsidea-video-control__seekbar-fill-rest"
        );
        this._thumb = this._element.querySelector(
            ".jsidea-video-control__seekbar-thumb"
        );
        this._tooltip = this._element.querySelector(
            ".jsidea-video-control__seekbar-tooltip"
        );
        this._tooltipLabel = this._element.querySelector(
            ".jsidea-video-control__seekbar-tooltip-label"
        );

        this._disposables.push(
            event.on({
                type: "keydown",
                scope: this._thumb,
                listener: (e) => {
                    if (!this._videoControl.ready) {
                        return;
                    }

                    if (KeyCode.is(e.event, KeyCode.LEFT_ARROW)) {
                        e.event.preventDefault();
                        const video = this._videoControl.video;
                        video.currentTime = Math.max(0, video.currentTime - 5);
                        this._videoControl.onPlayFrame.dispatch();
                    } else if (KeyCode.is(e.event, KeyCode.RIGHT_ARROW)) {
                        e.event.preventDefault();
                        const video = this._videoControl.video;
                        video.currentTime = Math.min(
                            video.duration,
                            video.currentTime + 5
                        );
                        this._videoControl.onPlayFrame.dispatch();
                    }
                },
            })
        );

        const actor = new Actor(this._element);
        this._actor = actor;
        actor.preventDefault = true;

        const getPointerPercentage = (pointerX: number): number => {
            const rect = this._track.getBoundingClientRect();
            const x = pointerX - rect.left;
            return jsidea.Calc.clamp(x / rect.width, 0, 1) * 100;
        };

        const getVideoPercentage = (): number => {
            if (!this._videoControl.video) {
                return 0;
            }
            const currentTime = this._videoControl.video.currentTime;
            const duration = this._videoControl.video.duration;
            if (duration > 0) {
                return jsidea.Calc.clamp(currentTime / duration, 0, 1) * 100;
            }
            return 0;
        };

        const updateFill = () => {
            if (!this._videoControl.video) {
                return;
            }

            let percentage = 0;
            if (actor.pointers.length > 0) {
                percentage = getPointerPercentage(actor.pointer.x);
                const time =
                    (percentage / 100) * this._videoControl.video.duration;
                this._tooltipLabel.textContent = formatTime(time);
            } else {
                percentage = getVideoPercentage();
            }

            this._fill.style.width = `${percentage}%`;
            this._fillRest.style.left = `${percentage}%`;
            this._thumb.style.left = `${percentage}%`;
        };

        actor.onStart.add(updateFill);
        actor.onMove.add(updateFill);

        actor.onStart.add(() => {
            this._thumb.classList.add(
                "jsidea-video-control__seekbar-thumb--dragging"
            );
        });

        actor.onClear.add(() => {
            this._thumb.classList.remove(
                "jsidea-video-control__seekbar-thumb--dragging"
            );
            if (!this._videoControl.ready || actor.pointers.length > 0) {
                return;
            }
            const ratio = getPointerPercentage(actor.pointer.x) / 100;
            const time = ratio * this._videoControl.video.duration;
            this._videoControl.video.currentTime = time;
        });

        this._disposables.push(
            this._videoControl.onPlayFrame.add(() => {
                if (!this._videoControl.ready || actor.pointers.length > 0) {
                    return;
                }
                updateFill();
            })
        );
    }

    public preventIdle(): boolean {
        return this._actor.pointers.length > 0;
    }

    public dispose(): void {
        super.dispose();
        this._track = null;
        this._fill = null;
        this._fillRest = null;
        this._thumb = null;
        this._tooltip = null;
        this._tooltipLabel = null;
        this._actor.dispose();
        this._actor = null;
    }
}

interface ToggleButtonOptions extends VideoControlButtonOptions {}

class ToggleButton extends VideoControlButton {
    constructor(options: ToggleButtonOptions) {
        super(options);

        this._element.classList.add(
            "jsidea-video-control__item--toggle-button"
        );

        this._videoControl.onStateChange.add(() => {
            this.updateIcon();
        });

        this.updateIcon();

        this._disposables.push(
            event.on({
                type: "click",
                scope: this._button,
                listener: (e) => {
                    if (!this._videoControl.video) {
                        return;
                    }

                    if (this._videoControl.paused) {
                        this._videoControl.video.play();
                    } else {
                        this._videoControl.video.pause();
                    }
                },
            })
        );
    }

    private updateIcon(): void {
        if (this._videoControl.paused) {
            this.icon = "fas fa-play";
        } else {
            this.icon = "fas fa-pause";
        }
    }
}

interface VolumeButtonOptions extends VideoControlButtonOptions {}

class VolumeButton extends VideoControlButton {
    constructor(options: VolumeButtonOptions) {
        super(options);

        this._element.classList.add(
            "jsidea-video-control__item--volume-button"
        );

        this._videoControl.bindToVideo((video, disposables) => {
            disposables.push(
                event.on({
                    type: "volumechange",
                    scope: video,
                    listener: () => {
                        this.updateIcon();
                    },
                })
            );
        });

        this.updateIcon();

        this._disposables.push(
            event.on({
                type: "click",
                scope: this._button,
                listener: (e) => {
                    if (!this._videoControl.video) {
                        return;
                    }
                    this._videoControl.video.muted =
                        !this._videoControl.video.muted;
                },
            })
        );
    }

    private updateIcon(): void {
        if (
            this._videoControl.video.muted ||
            this._videoControl.video.volume === 0
        ) {
            this.icon = "fas fa-volume-mute";
        } else {
            this.icon = "fas fa-volume-up";
        }
    }
}

interface FullscreenButtonOptions extends VideoControlButtonOptions {
    container: HTMLElement;
}

class FullscreenButton extends VideoControlButton {
    private _container: HTMLElement;

    constructor(options: FullscreenButtonOptions) {
        super(options);

        this._container = options.container;

        this._element.classList.add(
            "jsidea-video-control__item--fullscreen-button"
        );

        this._videoControl.onStateChange.add(() => {
            this.updateIcon();
        });

        this._disposables.push(
            FullscreenUtil.onChange.add((isFullscreen) => {
                this.updateIcon();
            })
        );

        this.updateIcon();

        this._disposables.push(
            event.on({
                type: "click",
                scope: this._button,
                listener: (e) => {
                    const hasFullscreen =
                        FullscreenUtil.isFullscreen &&
                        FullscreenUtil.element === this._container;

                    if (!hasFullscreen) {
                        FullscreenUtil.enter(this._container);
                    } else {
                        FullscreenUtil.exit();
                    }
                },
            })
        );
    }

    private updateIcon(): void {
        const hasFullscreen =
            FullscreenUtil.isFullscreen &&
            FullscreenUtil.element === this._container;

        this._container.classList.toggle(
            "jsidea-video-control-container--fullscreen",
            hasFullscreen
        );

        if (!hasFullscreen) {
            this.icon = "fas fa-expand";
        } else {
            this.icon = "fas fa-compress";
        }
    }

    public static get isSupported(): boolean {
        return FullscreenUtil.isSupported;
    }
}
